import { useEffect, useState } from 'react';
import { Tooltip } from '@mui/material';

import Card from '@/components/Card';
import { Body, Small } from '@/components/Typography';
import {
  ClientSession,
  fetchWorkerClientSessions,
} from '@/services/clientSessionsApi';

type Props = {
  workerId: number;
};

const formatHash = (h: string) =>
  h === 'embedded' ? 'embedded' : h.slice(0, 8);

const formatDate = (dateStr: string) => {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const ClientSessionsSection = ({ workerId }: Props) => {
  const [rows, setRows] = useState<ClientSession[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchWorkerClientSessions(workerId)
      .then((res) => {
        if (!cancelled) setRows(res.data);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        const msg =
          e instanceof Error ? e.message : 'Failed to load client sessions';
        setError(msg);
      });
    return () => {
      cancelled = true;
    };
  }, [workerId]);

  if (error) {
    return (
      <Card title="Client Sessions">
        <Card.Section>
          <Body color="lighter">{error}</Body>
        </Card.Section>
      </Card>
    );
  }

  if (rows === null) {
    return (
      <Card title="Client Sessions">
        <Card.Section>
          <Body>Loading...</Body>
        </Card.Section>
      </Card>
    );
  }

  if (rows.length === 0) {
    return (
      <Card title="Client Sessions">
        <Card.Section>
          <Body color="lighter">No client sessions recorded.</Body>
        </Card.Section>
      </Card>
    );
  }

  return (
    <Card title="Client Sessions">
      <Card.Section>
        <div style={{ overflowX: 'auto' }}>
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '14px',
            }}
          >
            <thead>
              <tr style={{ textAlign: 'left', color: '#6B7280' }}>
                <th style={{ padding: '8px 12px', fontWeight: 600 }}>
                  Platform
                </th>
                <th style={{ padding: '8px 12px', fontWeight: 600 }}>Binary</th>
                <th style={{ padding: '8px 12px', fontWeight: 600 }}>
                  OTA hash
                </th>
                <th style={{ padding: '8px 12px', fontWeight: 600 }}>
                  First seen
                </th>
                <th style={{ padding: '8px 12px', fontWeight: 600 }}>
                  Last seen
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} style={{ borderTop: '1px solid #E5E7EB' }}>
                  <td style={{ padding: '8px 12px' }}>
                    <Body>{r.platform}</Body>
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    <Body>{r.binary_version}</Body>
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    <Tooltip title={r.ota_hash}>
                      <span>
                        <Body>{formatHash(r.ota_hash)}</Body>
                      </span>
                    </Tooltip>
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    <Small color="lighter">{formatDate(r.first_seen_at)}</Small>
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    <Small color="lighter">{formatDate(r.last_seen_at)}</Small>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card.Section>
    </Card>
  );
};

export default ClientSessionsSection;
